/**
 * Auth error → friendly i18n message mapping.
 *
 * Supabase / GoTrue surfaces raw technical strings ("Token has expired or is
 * invalid", "User already registered", "Failed to fetch", ...) that normal
 * users can't act on. This module maps a raw auth error to a stable i18next
 * key so every auth page shows the same clear, translated copy.
 *
 * The mapper is PURE and i18n-agnostic: it returns a key (+ optional
 * interpolation values), never a rendered string. Callers render it with
 * `t()`. This keeps it trivially unit-testable (raw error → expected key)
 * and avoids coupling the mapping table to the translation layer.
 *
 * @module features/auth/utils/authErrorMessage
 */

/**
 * The surface the error occurred on. Some raw errors are ambiguous
 * (e.g. an already-registered email vs. a genuinely new one), so the caller
 * passes context to resolve to the right friendly message.
 */
export type AuthErrorContext = 'signup' | 'login' | 'otp' | 'passwordReset' | 'generic';

/** Root i18n namespace for all shared auth error copy. */
const NS = 'pages.authErrors';

/** i18n keys produced by the mapper. Exported for tests + type-safety. */
export type AuthErrorKey =
  | `${typeof NS}.alreadyRegistered`
  | `${typeof NS}.invalidCredentials`
  | `${typeof NS}.invalidCode`
  | `${typeof NS}.expiredCode`
  | `${typeof NS}.rateLimited`
  | `${typeof NS}.network`
  | `${typeof NS}.invalidEmail`
  | `${typeof NS}.weakPassword`
  | `${typeof NS}.emailNotConfirmed`
  | `${typeof NS}.samePassword`
  | `${typeof NS}.userNotFound`
  | `${typeof NS}.generic`;

/** Result of mapping: an i18n key plus optional interpolation params. */
export interface AuthErrorMessage {
  key: AuthErrorKey;
  /** Interpolation values passed to `t(key, params)` when present. */
  params?: Record<string, string | number>;
}

/**
 * Loosely-typed view of a Supabase auth error. We don't rely on the concrete
 * `AuthError` class because errors can arrive as plain objects (e.g. across
 * an edge-function boundary) or as native `TypeError` for network failures.
 */
interface RawAuthErrorShape {
  message?: unknown;
  status?: unknown;
  code?: unknown;
  name?: unknown;
}

function asRawError(error: unknown): RawAuthErrorShape {
  if (typeof error === 'string') return { message: error };
  if (error && typeof error === 'object') return error as RawAuthErrorShape;
  return {};
}

function getMessage(raw: RawAuthErrorShape): string {
  return typeof raw.message === 'string' ? raw.message.toLowerCase() : '';
}

function getCode(raw: RawAuthErrorShape): string {
  return typeof raw.code === 'string' ? raw.code : '';
}

function getStatus(raw: RawAuthErrorShape): number | undefined {
  if (typeof raw.status === 'number') return raw.status;
  if (typeof raw.status === 'string' && raw.status.trim() !== '') {
    const parsed = Number(raw.status);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

const k = (suffix: string): AuthErrorKey => `${NS}.${suffix}` as AuthErrorKey;

/** Error codes that mean "this email already has an account". */
const ALREADY_REGISTERED_CODES = new Set([
  'email_exists',
  'user_already_exists',
  'identity_already_exists',
]);

/** Error codes that mean the caller is being rate limited. */
const RATE_LIMIT_CODES = new Set([
  'over_request_rate_limit',
  'over_email_send_rate_limit',
  'over_sms_send_rate_limit',
]);

/** Error codes for an expired OTP / recovery / magic-link flow. */
const EXPIRED_CODES = new Set([
  'otp_expired',
  'flow_state_expired',
  'session_expired',
]);

/** Error codes for a malformed / unaccepted email address. */
const INVALID_EMAIL_CODES = new Set([
  'email_address_invalid',
  'email_address_not_authorized',
]);

/**
 * Detect a browser network failure. `fetch` rejects with a `TypeError`
 * whose message is "Failed to fetch" (Chromium) / "Load failed" (WebKit) /
 * "NetworkError when attempting to fetch resource." (Firefox).
 */
function isNetworkError(raw: RawAuthErrorShape, message: string): boolean {
  if (raw.name === 'TypeError' && getStatus(raw) === undefined) {
    return (
      message.includes('failed to fetch') ||
      message.includes('load failed') ||
      message.includes('networkerror') ||
      message === ''
    );
  }
  return (
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('networkerror when attempting')
  );
}

/**
 * Map a raw Supabase/GoTrue auth error to a friendly, translatable message.
 *
 * Matching precedence (most robust first):
 *   1. Network failure (before any code/status checks).
 *   2. Known `error.code` values.
 *   3. HTTP `status` (429 → rate limit).
 *   4. Message-substring fallback (older SDKs / non-coded errors).
 *   5. Context-aware default, else a generic friendly message.
 *
 * @param error   The caught error (AuthError, plain object, string, or unknown).
 * @param context Where it happened, to disambiguate ambiguous errors.
 * @returns       An i18n key (+ optional params) — never a raw technical string.
 */
export function getAuthErrorMessage(
  error: unknown,
  context: AuthErrorContext = 'generic',
): AuthErrorMessage {
  const raw = asRawError(error);
  const message = getMessage(raw);
  const code = getCode(raw);
  const status = getStatus(raw);

  // 1. Network failure — highest priority, independent of context.
  if (isNetworkError(raw, message)) {
    return { key: k('network') };
  }

  // 2. Known error codes.
  if (code) {
    if (ALREADY_REGISTERED_CODES.has(code)) return { key: k('alreadyRegistered') };
    if (RATE_LIMIT_CODES.has(code)) return { key: k('rateLimited') };
    if (EXPIRED_CODES.has(code)) return { key: k('expiredCode') };
    if (INVALID_EMAIL_CODES.has(code)) return { key: k('invalidEmail') };
    if (code === 'invalid_credentials') return { key: k('invalidCredentials') };
    if (code === 'weak_password') return { key: k('weakPassword') };
    if (code === 'email_not_confirmed') return { key: k('emailNotConfirmed') };
    if (code === 'same_password') return { key: k('samePassword') };
    if (code === 'user_not_found') return { key: k('userNotFound') };
  }

  // 3. HTTP status.
  if (status === 429) return { key: k('rateLimited') };

  // 4. Message-substring fallback (order matters: specific before generic).
  if (message) {
    if (
      message.includes('already registered') ||
      message.includes('already been registered') ||
      message.includes('user already exists') ||
      message.includes('email address is already') ||
      message.includes('email already')
    ) {
      return { key: k('alreadyRegistered') };
    }
    if (
      message.includes('invalid login credentials') ||
      message.includes('invalid email or password')
    ) {
      return { key: k('invalidCredentials') };
    }
    if (message.includes('too many requests') || message.includes('rate limit')) {
      return { key: k('rateLimited') };
    }
    if (message.includes('email not confirmed')) {
      return { key: k('emailNotConfirmed') };
    }
    if (message.includes('password should be') || message.includes('weak password')) {
      return { key: k('weakPassword') };
    }
    if (
      message.includes('unable to validate email') ||
      message.includes('invalid email') ||
      message.includes('invalid format')
    ) {
      return { key: k('invalidEmail') };
    }
    if (
      message.includes('expired') &&
      (message.includes('token') || message.includes('code') || message.includes('otp'))
    ) {
      // "Token has expired or is invalid" is used by GoTrue for BOTH a wrong
      // code AND an expired one. On the OTP surface we bias toward "invalid
      // code" (the common case: user mistyped); elsewhere treat as expired.
      return { key: context === 'otp' ? k('invalidCode') : k('expiredCode') };
    }
    if (
      message.includes('invalid') &&
      (message.includes('token') || message.includes('otp') || message.includes('code'))
    ) {
      return { key: k('invalidCode') };
    }
  }

  // 5. Context-aware default.
  if (context === 'otp') return { key: k('invalidCode') };
  if (context === 'login') return { key: k('invalidCredentials') };

  return { key: k('generic') };
}
